import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  FileText,
  FileX,
  Link,
  Pencil,
  RefreshCw,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { ErrorState } from "@/components/shared/error-state";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useDisciplines } from "@/hooks/use-disciplines";
import type { MappedRole } from "@/hooks/use-staffing-requests";
import {
  useIntensitySuggestion,
  useLinkSolution,
  usePatchLine,
  useRejectRequest,
  useRetryStaffingRequest,
  useStaffingRequestDetail,
  useUpdateRequestTitle,
} from "@/hooks/use-staffing-requests";
import { jsonFetch } from "@/lib/json-fetch";
import { AccountContextPanel } from "./AccountContextPanel";
import { CommitDialog } from "./CommitDialog";
import { DecisionLog } from "./DecisionLog";
import { IntensitySuggestion } from "./IntensitySuggestion";
import { LineMappingRow } from "./LineMappingRow";
import { StaffingStatusBadge } from "./StaffingStatusBadge";

interface SolutionOption {
  id: string;
  name: string;
  account: { name: string };
}

interface Props {
  requestId: string;
  onBack: () => void;
}

const SOURCE_LABELS: Record<string, string> = {
  SOW: "SOW",
  ResourcePlan: "Resource Plan",
  ExternalHook: "External Hook",
  Template: "Template",
};

export function MappingWorkspace({ requestId, onBack }: Props) {
  const [commitOpen, setCommitOpen] = useState(false);
  const [solutionSearch, setSolutionSearch] = useState("");
  const [selectedSolutionId, setSelectedSolutionId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"lines" | "log">("lines");

  const { data, isLoading, isError, error, refetch } = useStaffingRequestDetail(requestId);
  const { data: disciplines } = useDisciplines();
  const linkSolution = useLinkSolution(requestId);
  const patchLine = usePatchLine(requestId);
  const rejectRequest = useRejectRequest(requestId);
  const retryRequest = useRetryStaffingRequest(requestId);

  const isLinked = data?.linkedSolution !== null;

  const { data: intensitySuggestion } = useIntensitySuggestion(requestId, !isLoading && !!isLinked);

  const { data: solutionsData } = useQuery<{ solutions: SolutionOption[] }>({
    queryKey: ["dashboard-solutions"],
    queryFn: () => jsonFetch<{ solutions: SolutionOption[] }>("/api/dashboard/solutions"),
    enabled: !isLinked,
  });

  const filteredSolutions = useMemo(() => {
    if (!solutionsData?.solutions) return [];
    const q = solutionSearch.trim().toLowerCase();
    if (!q) return solutionsData.solutions.slice(0, 20);
    return solutionsData.solutions
      .filter((s) => s.name.toLowerCase().includes(q) || s.account.name.toLowerCase().includes(q))
      .slice(0, 20);
  }, [solutionsData, solutionSearch]);

  const unmappedCount = useMemo(
    () => (data?.lines ?? []).filter((l) => l.lineStatus === "Unmapped").length,
    [data],
  );

  if (isLoading) {
    return (
      <div className="px-6 py-5 space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="px-6 py-5">
        <ErrorState
          message={error instanceof Error ? error.message : "Failed to load request."}
          onRetry={() => refetch()}
        />
      </div>
    );
  }

  const { request, lines, events, account, linkedSolution, periodColumns } = data;

  return (
    <div className="px-6 py-5">
      {/* Header */}
      <div className="mb-5">
        <button
          type="button"
          onClick={onBack}
          className="mb-3 flex items-center gap-1.5 text-[12px] text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to queue
        </button>

        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <EditableTitle
                requestId={requestId}
                title={request.title ?? request.originalFilename ?? "Untitled upload"}
                editable={request.status !== "Committed" && request.status !== "Rejected"}
              />
              <StaffingStatusBadge status={request.status} />
              <span className="text-[10px] text-muted-foreground">
                {SOURCE_LABELS[request.sourceType] ?? request.sourceType}
              </span>
            </div>
            <div className="mt-0.5 text-[12px] text-muted-foreground">
              {account?.name && <span>{account.name}</span>}
              {request.sourceStartMonth && (
                <span
                  className="ml-2 text-[11px]"
                  title="Template Start Month — when the suggested Intensity begins"
                >
                  · Starts {request.sourceStartMonth}
                </span>
              )}
              {request.originalFilename && (
                <span className="ml-2 text-[11px]">· {request.originalFilename}</span>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {/* View the original uploaded artifact (PDF/CSV preview, XLSX download). */}
            <Button asChild size="sm" variant="outline">
              <a
                href={`/api/staffing-requests/${requestId}/raw`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <FileText className="mr-1.5 h-3.5 w-3.5" />
                View source file
              </a>
            </Button>
            {request.status !== "Committed" && request.status !== "Rejected" && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => rejectRequest.mutate(undefined)}
                  disabled={rejectRequest.isPending}
                >
                  Reject
                </Button>
                {isLinked && (
                  <Button
                    size="sm"
                    onClick={() => setCommitOpen(true)}
                    disabled={lines.length === 0}
                  >
                    Commit
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Parse Error state */}
      {request.status === "Error" && request.parseError && (
        <Alert variant="destructive" className="mb-5">
          <FileX />
          <AlertTitle>Parse Error</AlertTitle>
          <AlertDescription>
            <p>{request.parseError}</p>
            {request.originalFilename && (
              <p className="mt-1 text-[11px]">
                File:{" "}
                <a
                  href={`/api/staffing-requests/${requestId}/raw`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                >
                  {request.originalFilename}
                </a>
              </p>
            )}
            <Button
              variant="outline"
              size="sm"
              className="mt-3"
              disabled={retryRequest.isPending}
              onClick={() =>
                retryRequest.mutate(undefined, {
                  // A recovered re-parse supersedes this request and creates a new
                  // head — return to the queue, where the recovered request appears.
                  onSuccess: (result) => {
                    if (result.request.status !== "Error") onBack();
                  },
                })
              }
            >
              <RefreshCw
                className={`h-3.5 w-3.5 ${retryRequest.isPending ? "animate-spin" : ""}`}
              />
              {retryRequest.isPending ? "Retrying…" : "Retry parse"}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Link-to-Solution panel (when unlinked) */}
      {!isLinked && request.status !== "Error" && (
        <div className="mb-5 rounded-md border border-warning-200 bg-warning-50 px-4 py-3 dark:border-warning-900/40 dark:bg-warning-950/30">
          <div className="mb-2 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-warning-500" />
            <p className="text-[12px] font-semibold text-warning-700 dark:text-warning-300">
              Unlinked — no Solution assigned
            </p>
          </div>
          <p className="mb-3 text-[11px] text-warning-600 dark:text-warning-400">
            Link this request to a Solution before mapping lines.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <input
              value={solutionSearch}
              onChange={(e) => setSolutionSearch(e.target.value)}
              placeholder="Search solutions…"
              className="h-8 w-56 rounded-md border border-warning-200 bg-background px-3 text-[12px] outline-none focus:border-warning-400 dark:border-warning-800 dark:bg-warning-950/50"
            />
            <select
              value={selectedSolutionId ?? ""}
              onChange={(e) => setSelectedSolutionId(e.target.value || null)}
              className="h-8 min-w-[180px] rounded-md border border-warning-200 bg-background px-2 text-[12px] outline-none focus:border-warning-400 dark:border-warning-800 dark:bg-warning-950/50"
            >
              <option value="">Select Solution…</option>
              {filteredSolutions.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} — {s.account.name}
                </option>
              ))}
            </select>
            <Button
              size="sm"
              onClick={() => {
                if (selectedSolutionId) {
                  linkSolution.mutate(selectedSolutionId, {
                    onSuccess: () => {
                      setSelectedSolutionId(null);
                      setSolutionSearch("");
                    },
                  });
                }
              }}
              disabled={!selectedSolutionId || linkSolution.isPending}
            >
              <Link className="mr-1.5 h-3.5 w-3.5" />
              {linkSolution.isPending ? "Linking…" : "Link Solution"}
            </Button>
          </div>
        </div>
      )}

      {/* Linked Solution badge + its CURRENT team (Workflow 2: an existing Solution
          keeps its team by default — changes are flagged per line below). */}
      {isLinked && linkedSolution && (
        <div className="mb-4 space-y-1.5 text-[12px]">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">Solution:</span>
            <span className="font-medium">{linkedSolution.name}</span>
          </div>
          {data.currentPod.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-muted-foreground">Current team:</span>
              {[...data.currentPod]
                .sort((a, b) => a.role.localeCompare(b.role))
                .map((m) => (
                  <span
                    key={`${m.role}-${m.resourceId}`}
                    className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[11px]"
                    title="Active assignment on this Solution — kept unless a line replaces it"
                  >
                    <span className="text-[10px] font-semibold text-brand-700 dark:text-brand-300">
                      {m.role}
                    </span>
                    {m.resourceName}
                  </span>
                ))}
            </div>
          )}
        </div>
      )}

      {/* Account context — inherited Expectation Level + constraint defaults vs
          template suggestions (Story: Map to Solution inherits Account context). */}
      {request.status !== "Error" && <AccountContextPanel detail={data} />}

      {/* Intensity suggestion */}
      {isLinked && intensitySuggestion && (
        <div className="mb-4">
          <IntensitySuggestion suggestion={intensitySuggestion} />
        </div>
      )}

      {/* Tabs */}
      {request.status !== "Error" && (
        <>
          <div className="mb-3 flex gap-0 border-b border-border">
            <TabButton
              label={`Lines (${lines.length})`}
              active={activeTab === "lines"}
              onClick={() => setActiveTab("lines")}
            />
            <TabButton
              label={`Decision Log (${events.length})`}
              active={activeTab === "log"}
              onClick={() => setActiveTab("log")}
            />
          </div>

          {activeTab === "lines" && (
            <div className="overflow-hidden rounded-md border border-border bg-card">
              {lines.length === 0 ? (
                <div className="px-6 py-10 text-center text-[12px] text-muted-foreground">
                  No lines parsed for this request.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-[12px]">
                    <thead className="bg-muted/40 text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2 font-semibold">#</th>
                        <th className="px-3 py-2 font-semibold">Source Role</th>
                        {/* One column per period — the horizontal hours layout */}
                        {periodColumns.map((col) => (
                          <th
                            key={col}
                            className="px-3 py-2 text-right font-semibold whitespace-nowrap"
                          >
                            {col}
                          </th>
                        ))}
                        <th className="px-3 py-2 text-right font-semibold">Total</th>
                        <th className="px-3 py-2 font-semibold">Suggestion</th>
                        <th className="px-3 py-2 font-semibold">Discipline</th>
                        <th className="px-3 py-2 font-semibold">Position</th>
                        <th className="px-3 py-2 font-semibold">Resource</th>
                        <th className="px-3 py-2 font-semibold">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((line) => (
                        <LineMappingRow
                          key={line.id}
                          line={line}
                          disciplines={disciplines ?? []}
                          currentPod={data.currentPod}
                          periodColumns={periodColumns}
                          onPatch={(payload) => {
                            patchLine.mutate({
                              lineId: payload.lineId,
                              mappedDisciplineId: payload.mappedDisciplineId,
                              mappedRole: payload.mappedRole as MappedRole | undefined,
                              mappedResourceId: payload.mappedResourceId,
                            });
                          }}
                          isPending={patchLine.isPending}
                        />
                      ))}
                    </tbody>
                    {/* Total requested hours by month across all lines (context only —
                        hours are never converted to points). */}
                    <tfoot className="border-t-2 border-border bg-muted/40">
                      <tr>
                        <td
                          colSpan={2}
                          className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground"
                        >
                          Total hours
                        </td>
                        {periodColumns.map((col) => (
                          <td
                            key={col}
                            className="px-3 py-2 text-right text-[11px] font-semibold text-foreground"
                          >
                            {lines
                              .reduce(
                                (sum, l) =>
                                  sum + (l.sourcePeriods.find((p) => p.period === col)?.hours ?? 0),
                                0,
                              )
                              .toLocaleString()}
                          </td>
                        ))}
                        <td className="px-3 py-2 text-right text-[11px] font-bold text-foreground">
                          {lines.reduce((sum, l) => sum + l.sourceHours, 0).toLocaleString()} hrs
                        </td>
                        <td colSpan={5} />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === "log" && (
            <div className="rounded-md border border-border bg-card px-4 py-2">
              <DecisionLog events={events} />
            </div>
          )}
        </>
      )}

      <CommitDialog
        open={commitOpen}
        onOpenChange={setCommitOpen}
        requestId={requestId}
        suggestedIntensity={request.suggestedIntensity}
        confirmedIntensity={request.confirmedIntensity}
        startMonth={request.sourceStartMonth}
        unmappedCount={unmappedCount}
      />
    </div>
  );
}

/** The request title as a click-to-edit label (Enter/✓ saves, Esc/✕ cancels). */
function EditableTitle({
  requestId,
  title,
  editable,
}: {
  requestId: string;
  title: string;
  editable: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(title);
  const inputRef = useRef<HTMLInputElement>(null);
  const updateTitle = useUpdateRequestTitle(requestId);

  // Focus the field when entering edit mode (not a data effect).
  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  function save() {
    const next = value.trim();
    if (next.length === 0 || next === title) {
      setValue(title);
      setEditing(false);
      return;
    }
    updateTitle.mutate(next, { onSuccess: () => setEditing(false) });
  }

  function cancel() {
    setValue(title);
    setEditing(false);
  }

  if (!editing) {
    return (
      <span className="flex items-center gap-1.5">
        <h1 className="font-display text-[18px] font-bold leading-tight">{title}</h1>
        {editable && (
          <button
            type="button"
            onClick={() => {
              setValue(title);
              setEditing(true);
            }}
            className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            aria-label="Rename request"
            title="Rename request"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
      </span>
    );
  }

  return (
    <span className="flex items-center gap-1">
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") save();
          if (e.key === "Escape") cancel();
        }}
        maxLength={200}
        disabled={updateTitle.isPending}
        className="h-8 w-64 rounded-md border border-border bg-card px-2 font-display text-[16px] font-bold outline-none focus:border-foreground/40"
      />
      <button
        type="button"
        onClick={save}
        disabled={updateTitle.isPending}
        className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40"
        aria-label="Save title"
      >
        <Check className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        onClick={cancel}
        disabled={updateTitle.isPending}
        className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40"
        aria-label="Cancel rename"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </span>
  );
}

function TabButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "px-4 py-2 text-[12px] font-medium transition-colors",
        active
          ? "border-b-2 border-foreground text-foreground"
          : "text-muted-foreground hover:text-foreground",
      ].join(" ")}
    >
      {label}
    </button>
  );
}
